import React, { useState, useRef, useEffect, useCallback } from "react";
import * as THREE from "three";

/* ======================================================================
   STL parsing
   ====================================================================== */

function parseSTL(buffer) {
  const dv = new DataView(buffer);

  function looksBinary() {
    if (buffer.byteLength < 84) return false;
    const triCount = dv.getUint32(80, true);
    const expected = 84 + triCount * 50;
    if (expected === buffer.byteLength) return true;
    // Fallback: ascii files start with "solid"
    const head = new TextDecoder().decode(buffer.slice(0, Math.min(5, buffer.byteLength)));
    return head.toLowerCase() !== "solid";
  }

  if (looksBinary()) {
    const triCount = dv.getUint32(80, true);
    const positions = new Float32Array(triCount * 9);
    let offset = 84;
    for (let i = 0; i < triCount; i++) {
      offset += 12; // skip stored facet normal, we recompute our own
      for (let v = 0; v < 3; v++) {
        const base = i * 9 + v * 3;
        positions[base] = dv.getFloat32(offset, true);
        offset += 4;
        positions[base + 1] = dv.getFloat32(offset, true);
        offset += 4;
        positions[base + 2] = dv.getFloat32(offset, true);
        offset += 4;
      }
      offset += 2; // attribute byte count
    }
    return positions;
  }

  // ASCII STL
  const text = new TextDecoder().decode(buffer);
  const verts = [];
  const re = /vertex\s+([-\d.eE+]+)\s+([-\d.eE+]+)\s+([-\d.eE+]+)/g;
  let m;
  while ((m = re.exec(text)) !== null) {
    verts.push(parseFloat(m[1]), parseFloat(m[2]), parseFloat(m[3]));
  }
  return new Float32Array(verts);
}

function writeSTLBinary(positions) {
  const triCount = positions.length / 9;
  const buffer = new ArrayBuffer(84 + triCount * 50);
  const dv = new DataView(buffer);
  dv.setUint32(80, triCount, true);
  let offset = 84;
  for (let t = 0; t < triCount; t++) {
    const base = t * 9;
    const ax = positions[base], ay = positions[base + 1], az = positions[base + 2];
    const bx = positions[base + 3], by = positions[base + 4], bz = positions[base + 5];
    const cx = positions[base + 6], cy = positions[base + 7], cz = positions[base + 8];
    const ux = bx - ax, uy = by - ay, uz = bz - az;
    const vx = cx - ax, vy = cy - ay, vz = cz - az;
    let nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
    const len = Math.hypot(nx, ny, nz) || 1;
    nx /= len; ny /= len; nz /= len;
    dv.setFloat32(offset, nx, true); offset += 4;
    dv.setFloat32(offset, ny, true); offset += 4;
    dv.setFloat32(offset, nz, true); offset += 4;
    const verts = [[ax, ay, az], [bx, by, bz], [cx, cy, cz]];
    for (let k = 0; k < 3; k++) {
      dv.setFloat32(offset, verts[k][0], true); offset += 4;
      dv.setFloat32(offset, verts[k][1], true); offset += 4;
      dv.setFloat32(offset, verts[k][2], true); offset += 4;
    }
    dv.setUint16(offset, 0, true); offset += 2;
  }
  return buffer;
}

/* ======================================================================
   Geometry math helpers
   ====================================================================== */

function computeVolumeMm3(positions) {
  let vol = 0;
  for (let i = 0; i < positions.length; i += 9) {
    const x0 = positions[i], y0 = positions[i + 1], z0 = positions[i + 2];
    const x1 = positions[i + 3], y1 = positions[i + 4], z1 = positions[i + 5];
    const x2 = positions[i + 6], y2 = positions[i + 7], z2 = positions[i + 8];
    vol += (x0 * (y1 * z2 - z1 * y2) - y0 * (x1 * z2 - z1 * x2) + z0 * (x1 * y2 - y1 * x2)) / 6;
  }
  return Math.abs(vol);
}

function computeBBox(positions) {
  const min = [Infinity, Infinity, Infinity];
  const max = [-Infinity, -Infinity, -Infinity];
  for (let i = 0; i < positions.length; i += 3) {
    for (let a = 0; a < 3; a++) {
      const v = positions[i + a];
      if (v < min[a]) min[a] = v;
      if (v > max[a]) max[a] = v;
    }
  }
  return { min, max };
}

function bakeMatrix(positions, matrix4) {
  const v = new THREE.Vector3();
  const out = new Float32Array(positions.length);
  for (let i = 0; i < positions.length; i += 3) {
    v.set(positions[i], positions[i + 1], positions[i + 2]);
    v.applyMatrix4(matrix4);
    out[i] = v.x; out[i + 1] = v.y; out[i + 2] = v.z;
  }
  return out;
}

function sign2D(p1, p2, p3) {
  return (p1[0] - p3[0]) * (p2[1] - p3[1]) - (p2[0] - p3[0]) * (p1[1] - p3[1]);
}

function pointInTriangle2D(p, a, b, c) {
  const d1 = sign2D(p, a, b);
  const d2 = sign2D(p, b, c);
  const d3 = sign2D(p, c, a);
  const hasNeg = d1 < 0 || d2 < 0 || d3 < 0;
  const hasPos = d1 > 0 || d2 > 0 || d3 > 0;
  return !(hasNeg && hasPos);
}

// Clip a triangle soup by the plane (coord[axisIndex] == offset).
// Returns triangles on the positive side (>= offset), the negative side,
// and the raw boundary segments lying exactly on the cutting plane.
function clipPositions(positions, axisIndex, offset) {
  const posOut = [];
  const negOut = [];
  const segments = [];
  const triCount = positions.length / 9;

  for (let t = 0; t < triCount; t++) {
    const base = t * 9;
    const v = [
      [positions[base], positions[base + 1], positions[base + 2]],
      [positions[base + 3], positions[base + 4], positions[base + 5]],
      [positions[base + 6], positions[base + 7], positions[base + 8]],
    ];
    const d = [v[0][axisIndex] - offset, v[1][axisIndex] - offset, v[2][axisIndex] - offset];

    const posPoly = [];
    const negPoly = [];
    const crossPts = [];

    for (let i = 0; i < 3; i++) {
      const j = (i + 1) % 3;
      const vi = v[i], vj = v[j];
      const di = d[i], dj = d[j];
      if (di >= 0) posPoly.push(vi); else negPoly.push(vi);
      if ((di >= 0) !== (dj >= 0)) {
        const tt = di / (di - dj);
        const p = [
          vi[0] + (vj[0] - vi[0]) * tt,
          vi[1] + (vj[1] - vi[1]) * tt,
          vi[2] + (vj[2] - vi[2]) * tt,
        ];
        posPoly.push(p);
        negPoly.push(p);
        crossPts.push(p);
      }
    }

    if (crossPts.length === 2) {
      segments.push([crossPts[0], crossPts[1]]);
    }

    for (let k = 1; k + 1 < posPoly.length; k++) {
      posOut.push(posPoly[0][0], posPoly[0][1], posPoly[0][2]);
      posOut.push(posPoly[k][0], posPoly[k][1], posPoly[k][2]);
      posOut.push(posPoly[k + 1][0], posPoly[k + 1][1], posPoly[k + 1][2]);
    }
    for (let k = 1; k + 1 < negPoly.length; k++) {
      negOut.push(negPoly[0][0], negPoly[0][1], negPoly[0][2]);
      negOut.push(negPoly[k][0], negPoly[k][1], negPoly[k][2]);
      negOut.push(negPoly[k + 1][0], negPoly[k + 1][1], negPoly[k + 1][2]);
    }
  }

  return { posOut, negOut, segments };
}

function buildLoops(segments) {
  const key = (p) => p[0].toFixed(5) + "," + p[1].toFixed(5) + "," + p[2].toFixed(5);
  const edges = segments.map((s) => ({ a: s[0], b: s[1], ka: key(s[0]), kb: key(s[1]), used: false }));
  const byKey = new Map();
  edges.forEach((e) => {
    if (!byKey.has(e.ka)) byKey.set(e.ka, []);
    if (!byKey.has(e.kb)) byKey.set(e.kb, []);
    byKey.get(e.ka).push(e);
    byKey.get(e.kb).push(e);
  });

  const loops = [];
  for (const startEdge of edges) {
    if (startEdge.used) continue;
    startEdge.used = true;
    const loop = [startEdge.a];
    let currentPoint = startEdge.b;
    let currentKey = startEdge.kb;
    const startKey = startEdge.ka;
    let safety = 0;
    while (currentKey !== startKey && safety < edges.length + 5) {
      safety++;
      loop.push(currentPoint);
      const candidates = (byKey.get(currentKey) || []).filter((e) => !e.used);
      if (candidates.length === 0) break;
      const next = candidates[0];
      next.used = true;
      if (next.ka === currentKey) {
        currentPoint = next.b; currentKey = next.kb;
      } else {
        currentPoint = next.a; currentKey = next.ka;
      }
    }
    if (loop.length >= 3) loops.push(loop);
  }
  return loops;
}

// Ear-clip a (roughly) planar loop and return a flat triangle array,
// with winding chosen so the cap's normal points toward +axis (flip=true)
// or -axis (flip=false).
function triangulateLoop(loop, axisIndex, flip) {
  const idx = [0, 1, 2].filter((i) => i !== axisIndex);
  const pts2d = loop.map((p) => [p[idx[0]], p[idx[1]]]);

  let area = 0;
  for (let i = 0; i < pts2d.length; i++) {
    const [x1, y1] = pts2d[i];
    const [x2, y2] = pts2d[(i + 1) % pts2d.length];
    area += x1 * y2 - x2 * y1;
  }
  let order = loop.map((_, i) => i);
  if (area < 0) order = order.reverse();

  const remaining = order.slice();
  const triIdx = [];
  let guard = 0;
  while (remaining.length > 3 && guard < 8000) {
    guard++;
    let earFound = false;
    for (let i = 0; i < remaining.length; i++) {
      const iPrev = remaining[(i - 1 + remaining.length) % remaining.length];
      const iCur = remaining[i];
      const iNext = remaining[(i + 1) % remaining.length];
      const a = pts2d[iPrev], b = pts2d[iCur], c = pts2d[iNext];
      const cross = (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
      if (cross <= 1e-12) continue;
      let containsOther = false;
      for (let k = 0; k < remaining.length; k++) {
        const idxP = remaining[k];
        if (idxP === iPrev || idxP === iCur || idxP === iNext) continue;
        if (pointInTriangle2D(pts2d[idxP], a, b, c)) { containsOther = true; break; }
      }
      if (containsOther) continue;
      triIdx.push([iPrev, iCur, iNext]);
      remaining.splice(i, 1);
      earFound = true;
      break;
    }
    if (!earFound) break;
  }
  if (remaining.length === 3) triIdx.push([remaining[0], remaining[1], remaining[2]]);

  const out = [];
  triIdx.forEach(([i0, i1, i2]) => {
    let p0 = loop[i0], p1 = loop[i1], p2 = loop[i2];
    const e1 = [p1[0] - p0[0], p1[1] - p0[1], p1[2] - p0[2]];
    const e2 = [p2[0] - p0[0], p2[1] - p0[1], p2[2] - p0[2]];
    const n = [
      e1[1] * e2[2] - e1[2] * e2[1],
      e1[2] * e2[0] - e1[0] * e2[2],
      e1[0] * e2[1] - e1[1] * e2[0],
    ];
    if ((n[axisIndex] > 0) !== flip) {
      const tmp = p1; p1 = p2; p2 = tmp;
    }
    out.push(p0[0], p0[1], p0[2], p1[0], p1[1], p1[2], p2[0], p2[1], p2[2]);
  });
  return out;
}

function cutPositions(positions, axisIndex, offset) {
  const { posOut, negOut, segments } = clipPositions(positions, axisIndex, offset);
  const loops = buildLoops(segments);
  loops.forEach((loop) => {
    const capA = triangulateLoop(loop, axisIndex, false);
    const capB = triangulateLoop(loop, axisIndex, true);
    for (let i = 0; i < capA.length; i++) posOut.push(capA[i]);
    for (let i = 0; i < capB.length; i++) negOut.push(capB[i]);
  });
  return { pos: new Float32Array(posOut), neg: new Float32Array(negOut) };
}

function makeGeometry(positions) {
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geo.computeVertexNormals();
  geo.computeBoundingSphere();
  geo.computeBoundingBox();
  return geo;
}

const PALETTE = ["#5fd0c6", "#e8a33d", "#7c9ef2", "#e0708a", "#8fd16b", "#c98ee0", "#f2c464", "#63b6e6"];

/* ======================================================================
   Component
   ====================================================================== */

export default function () {
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const planeHelperRef = useRef(null);
  const gridRef = useRef(null);
  const partsRef = useRef([]); // { id, group, mesh, geometry, positions, color, visible, name }
  const idCounterRef = useRef(1);
  const camStateRef = useRef({ theta: Math.PI * 0.65, phi: Math.PI * 0.38, radius: 200, target: new THREE.Vector3(0, 0, 0) });
  const pointerRef = useRef({ down: false, button: 0, x: 0, y: 0 });
  const animRef = useRef(null);

  const [partsMeta, setPartsMeta] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [cutAxis, setCutAxis] = useState("x");
  const [cutOffset, setCutOffset] = useState(0);
  const [cutRange, setCutRange] = useState({ min: -50, max: 50 });
  const [rotateAngle, setRotateAngle] = useState(90);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [hasParts, setHasParts] = useState(false);

  /* ---------------- three.js scene bootstrap ---------------- */

  useEffect(() => {
    const mount = mountRef.current;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0a0a0c);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100000);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    rendererRef.current = renderer;
    mount.appendChild(renderer.domElement);

    const hemi = new THREE.HemisphereLight(0xffffff, 0x1a1a22, 0.65);
    scene.add(hemi);
    const key = new THREE.DirectionalLight(0xffffff, 0.9);
    scene.add(key);
    const fill = new THREE.DirectionalLight(0xaeeeff, 0.25);
    fill.position.set(-1, 0.5, -1);
    scene.add(fill);

    const grid = new THREE.GridHelper(200, 20, 0x2a2f35, 0x1c2024);
    grid.position.y = 0;
    scene.add(grid);
    gridRef.current = grid;

    const axes = new THREE.AxesHelper(30);
    scene.add(axes);

    function resize() {
      const w = mount.clientWidth || 1;
      const h = mount.clientHeight || 1;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    }
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(mount);

    function updateCamera() {
      const cs = camStateRef.current;
      const p = cs.phi;
      const t = cs.theta;
      const r = cs.radius;
      camera.position.set(
        cs.target.x + r * Math.sin(p) * Math.sin(t),
        cs.target.y + r * Math.cos(p),
        cs.target.z + r * Math.sin(p) * Math.cos(t)
      );
      camera.lookAt(cs.target);
      key.position.copy(camera.position);
    }
    updateCamera();

    function onPointerDown(e) {
      pointerRef.current = { down: true, button: e.button, x: e.clientX, y: e.clientY, shift: e.shiftKey };
    }
    function onPointerMove(e) {
      const pr = pointerRef.current;
      if (!pr.down) return;
      const dx = e.clientX - pr.x;
      const dy = e.clientY - pr.y;
      pr.x = e.clientX; pr.y = e.clientY;
      const cs = camStateRef.current;
      const pan = pr.button === 2 || pr.shift;
      if (pan) {
        const panSpeed = cs.radius * 0.0015;
        const right = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 0);
        const camUp = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 1);
        cs.target.addScaledVector(right, -dx * panSpeed);
        cs.target.addScaledVector(camUp, dy * panSpeed);
      } else {
        cs.theta -= dx * 0.006;
        cs.phi -= dy * 0.006;
        cs.phi = Math.max(0.06, Math.min(Math.PI - 0.06, cs.phi));
      }
      updateCamera();
    }
    function onPointerUp() {
      pointerRef.current.down = false;
    }
    function onWheel(e) {
      e.preventDefault();
      const cs = camStateRef.current;
      cs.radius *= 1 + Math.sign(e.deltaY) * 0.08;
      cs.radius = Math.max(2, Math.min(5000, cs.radius));
      updateCamera();
    }
    function onContextMenu(e) { e.preventDefault(); }

    const dom = renderer.domElement;
    dom.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    dom.addEventListener("wheel", onWheel, { passive: false });
    dom.addEventListener("contextmenu", onContextMenu);

    function loop() {
      renderer.render(scene, camera);
      animRef.current = requestAnimationFrame(loop);
    }
    loop();

    return () => {
      cancelAnimationFrame(animRef.current);
      ro.disconnect();
      dom.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      dom.removeEventListener("wheel", onWheel);
      dom.removeEventListener("contextmenu", onContextMenu);
      renderer.dispose();
      if (mount.contains(dom)) mount.removeChild(dom);
    };
  }, []);

  /* ---------------- helpers over the live scene ---------------- */

  const syncMeta = useCallback(() => {
    setPartsMeta(
      partsRef.current.map((p) => ({
        id: p.id,
        name: p.name,
        color: p.color,
        visible: p.visible,
        volume: computeVolumeMm3(p.positions),
        triCount: p.positions.length / 9,
      }))
    );
    setHasParts(partsRef.current.length > 0);
  }, []);

  const fitView = useCallback(() => {
    const visible = partsRef.current.filter((p) => p.visible);
    if (visible.length === 0) return;
    const min = [Infinity, Infinity, Infinity];
    const max = [-Infinity, -Infinity, -Infinity];
    visible.forEach((p) => {
      const bb = computeBBox(p.positions);
      for (let a = 0; a < 3; a++) {
        min[a] = Math.min(min[a], bb.min[a]);
        max[a] = Math.max(max[a], bb.max[a]);
      }
    });
    const center = new THREE.Vector3((min[0] + max[0]) / 2, (min[1] + max[1]) / 2, (min[2] + max[2]) / 2);
    const size = Math.max(max[0] - min[0], max[1] - min[1], max[2] - min[2], 1);
    camStateRef.current.target.copy(center);
    camStateRef.current.radius = size * 1.8;
    const grid = gridRef.current;
    if (grid) {
      const gridSize = Math.max(size * 3, 50);
      grid.geometry.dispose();
      grid.geometry = new THREE.GridHelper(gridSize, 20).geometry;
      grid.position.set(center.x, min[1], center.z);
    }
  }, []);

  function addPart(positions, name) {
    const scene = sceneRef.current;
    const geometry = makeGeometry(positions);
    const color = PALETTE[(idCounterRef.current - 1) % PALETTE.length];
    const material = new THREE.MeshStandardMaterial({
      color,
      flatShading: true,
      roughness: 0.55,
      metalness: 0.05,
      side: THREE.DoubleSide,
    });
    const mesh = new THREE.Mesh(geometry, material);
    const group = new THREE.Group();
    group.add(mesh);
    scene.add(group);

    const part = {
      id: idCounterRef.current++,
      group,
      mesh,
      geometry,
      positions,
      color,
      visible: true,
      name,
    };
    partsRef.current.push(part);
    return part;
  }

  function removePartById(id) {
    const idx = partsRef.current.findIndex((p) => p.id === id);
    if (idx === -1) return;
    const p = partsRef.current[idx];
    sceneRef.current.remove(p.group);
    p.geometry.dispose();
    p.mesh.material.dispose();
    partsRef.current.splice(idx, 1);
  }

  /* ---------------- file upload ---------------- */

  const handleFile = useCallback((e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setError("");
    setBusy(true);
    const reader = new FileReader();
    reader.onerror = () => {
      setBusy(false);
      setError("Не удалось прочитать файл.");
    };
    reader.onload = () => {
      try {
        const positions = parseSTL(reader.result);
        if (!positions || positions.length < 9) {
          throw new Error("empty");
        }
        const baseName = file.name.replace(/\.stl$/i, "");
        const part = addPart(positions, baseName);
        setSelectedId(part.id);
        syncMeta();
        fitView();
      } catch (err) {
        setError("Не удалось разобрать STL-файл. Проверьте, что это корректный ASCII или бинарный STL.");
      } finally {
        setBusy(false);
      }
    };
    reader.readAsArrayBuffer(file);
  }, [syncMeta, fitView]);

  /* ---------------- part list actions ---------------- */

  const toggleVisible = useCallback((id) => {
    const p = partsRef.current.find((pp) => pp.id === id);
    if (!p) return;
    p.visible = !p.visible;
    p.group.visible = p.visible;
    syncMeta();
  }, [syncMeta]);

  const deletePart = useCallback((id) => {
    removePartById(id);
    setSelectedId((cur) => (cur === id ? null : cur));
    syncMeta();
  }, [syncMeta]);

  const downloadPart = useCallback((id) => {
    const p = partsRef.current.find((pp) => pp.id === id);
    if (!p) return;
    const buffer = writeSTLBinary(p.positions);
    const blob = new Blob([buffer], { type: "application/sla" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = p.name + ".stl";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  /* ---------------- alignment (bake rotation into vertices) ---------------- */

  const rotateSelected = useCallback((axis, dir) => {
    const p = partsRef.current.find((pp) => pp.id === selectedId);
    if (!p) { setError("Сначала выберите модель в списке."); return; }
    const rad = THREE.MathUtils.degToRad(rotateAngle * dir);
    const m = new THREE.Matrix4();
    if (axis === "x") m.makeRotationX(rad);
    else if (axis === "y") m.makeRotationY(rad);
    else m.makeRotationZ(rad);
    p.positions = bakeMatrix(p.positions, m);
    p.geometry.setAttribute("position", new THREE.BufferAttribute(p.positions, 3));
    p.geometry.computeVertexNormals();
    p.geometry.computeBoundingSphere();
    p.geometry.computeBoundingBox();
    syncMeta();
    if (p.id === selectedId) updateCutRangeFor(p);
  }, [selectedId, rotateAngle]);

  const centerSelected = useCallback(() => {
    const p = partsRef.current.find((pp) => pp.id === selectedId);
    if (!p) { setError("Сначала выберите модель в списке."); return; }
    const bb = computeBBox(p.positions);
    const cx = (bb.min[0] + bb.max[0]) / 2;
    const cy = (bb.min[1] + bb.max[1]) / 2;
    const cz = (bb.min[2] + bb.max[2]) / 2;
    const m = new THREE.Matrix4().makeTranslation(-cx, -cy, -cz);
    p.positions = bakeMatrix(p.positions, m);
    p.geometry.setAttribute("position", new THREE.BufferAttribute(p.positions, 3));
    p.geometry.computeVertexNormals();
    p.geometry.computeBoundingSphere();
    p.geometry.computeBoundingBox();
    syncMeta();
    updateCutRangeFor(p);
    fitView();
  }, [selectedId, fitView]);

  /* ---------------- cutting ---------------- */

  function updateCutRangeFor(part) {
    if (!part) return;
    const bb = computeBBox(part.positions);
    const axisIdx = cutAxis === "x" ? 0 : cutAxis === "y" ? 1 : 2;
    const min = bb.min[axisIdx];
    const max = bb.max[axisIdx];
    setCutRange({ min, max });
    setCutOffset((min + max) / 2);
  }

  useEffect(() => {
    const p = partsRef.current.find((pp) => pp.id === selectedId);
    updateCutRangeFor(p);
  }, [selectedId, cutAxis]);

  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    if (planeHelperRef.current) {
      scene.remove(planeHelperRef.current);
      planeHelperRef.current.geometry.dispose();
      planeHelperRef.current.material.dispose();
      planeHelperRef.current = null;
    }
    const p = partsRef.current.find((pp) => pp.id === selectedId);
    if (!p) return;
    const bb = computeBBox(p.positions);
    const size = Math.max(bb.max[0] - bb.min[0], bb.max[1] - bb.min[1], bb.max[2] - bb.min[2], 1) * 1.6;
    const geo = new THREE.PlaneGeometry(size, size);
    const mat = new THREE.MeshBasicMaterial({ color: 0xf2c464, transparent: true, opacity: 0.22, side: THREE.DoubleSide, depthWrite: false });
    const plane = new THREE.Mesh(geo, mat);
    const cx = (bb.min[0] + bb.max[0]) / 2;
    const cy = (bb.min[1] + bb.max[1]) / 2;
    const cz = (bb.min[2] + bb.max[2]) / 2;
    if (cutAxis === "x") {
      plane.position.set(cutOffset, cy, cz);
      plane.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), new THREE.Vector3(1, 0, 0));
    } else if (cutAxis === "y") {
      plane.position.set(cx, cutOffset, cz);
      plane.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), new THREE.Vector3(0, 1, 0));
    } else {
      plane.position.set(cx, cy, cutOffset);
      // plane already faces +Z, no rotation needed
    }
    scene.add(plane);
    planeHelperRef.current = plane;
  }, [selectedId, cutAxis, cutOffset]);

  const performCut = useCallback(() => {
    const p = partsRef.current.find((pp) => pp.id === selectedId);
    if (!p) { setError("Сначала выберите модель в списке."); return; }
    setError("");
    setBusy(true);
    setTimeout(() => {
      try {
        const axisIdx = cutAxis === "x" ? 0 : cutAxis === "y" ? 1 : 2;
        const { pos, neg } = cutPositions(p.positions, axisIdx, cutOffset);
        if (pos.length === 0 || neg.length === 0) {
          setError("Плоскость не пересекает модель — сдвиньте её в пределах модели.");
          setBusy(false);
          return;
        }
        const baseName = p.name;
        removePartById(p.id);
        const partA = addPart(pos, baseName + " · A");
        addPart(neg, baseName + " · B");
        setSelectedId(partA.id);
        syncMeta();
      } catch (err) {
        setError("Не удалось выполнить разрез этой модели. Возможно, сетка не замкнута (не watertight).");
      } finally {
        setBusy(false);
      }
    }, 10);
  }, [selectedId, cutAxis, cutOffset, syncMeta]);

  /* ---------------- render ---------------- */

  const totalVolume = partsMeta.reduce((s, p) => s + p.volume, 0);

  return (
    <div className="w-full h-screen bg-zinc-950 text-zinc-100 flex overflow-hidden font-sans">
      <div className="flex-1 relative">
        <div ref={mountRef} className="absolute inset-0" />

        {!hasParts && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center max-w-sm px-6">
              <p className="text-zinc-300 text-base mb-1">Загрузите STL-модель нижней челюсти</p>
              <p className="text-zinc-500 text-sm">Вращение — ЛКМ, панорама — ПКМ или Shift, зум — колесо мыши</p>
            </div>
          </div>
        )}

        <div className="absolute top-3 right-3 flex gap-2">
          <button
            onClick={fitView}
            className="px-3 py-1.5 text-sm rounded-md bg-zinc-900/80 border border-zinc-700 hover:bg-zinc-800 text-zinc-200"
          >
            Показать всё
          </button>
        </div>

        {busy && (
          <div className="absolute bottom-3 left-3 px-3 py-1.5 text-sm rounded-md bg-zinc-900/90 border border-zinc-700 text-zinc-300">
            Обработка…
          </div>
        )}

        {error && (
          <div className="absolute bottom-3 left-3 right-3 max-w-md mx-auto px-3 py-2 text-sm rounded-md bg-rose-950/90 border border-rose-800 text-rose-200 flex items-start justify-between gap-3">
            <span>{error}</span>
            <button onClick={() => setError("")} className="text-rose-300 hover:text-rose-100 shrink-0">✕</button>
          </div>
        )}
      </div>

      <div className="w-[22rem] shrink-0 border-l border-zinc-800 bg-zinc-900 flex flex-col overflow-y-auto">
        <div className="px-4 py-4 border-b border-zinc-800">
          <h1 className="text-sm font-medium text-zinc-100">Просмотр и разрезание STL</h1>
          <p className="text-xs text-zinc-500 mt-1">Нижняя челюсть · миллиметры</p>
        </div>

        <div className="px-4 py-3 border-b border-zinc-800">
          <label className="block">
            <span className="block text-xs text-zinc-400 mb-1.5">Загрузить модель (.stl)</span>
            <input
              type="file"
              accept=".stl"
              onChange={handleFile}
              className="block w-full text-xs text-zinc-300 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-teal-600 file:text-white file:text-xs hover:file:bg-teal-500 cursor-pointer"
            />
          </label>
        </div>

        <div className="px-4 py-3 border-b border-zinc-800">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-zinc-400">Модели ({partsMeta.length})</span>
            {partsMeta.length > 0 && (
              <span className="text-xs text-zinc-500">
                суммарный объём {(totalVolume / 1000).toFixed(2)} см³
              </span>
            )}
          </div>
          <div className="flex flex-col gap-1.5 max-h-56 overflow-y-auto pr-1">
            {partsMeta.map((p) => (
              <div
                key={p.id}
                onClick={() => setSelectedId(p.id)}
                className={
                  "flex items-center gap-2 px-2 py-1.5 rounded-md border cursor-pointer text-xs " +
                  (p.id === selectedId
                    ? "border-teal-500 bg-teal-950/40"
                    : "border-zinc-800 bg-zinc-900 hover:bg-zinc-800")
                }
              >
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: p.color }} />
                <div className="flex-1 min-w-0">
                  <div className="truncate text-zinc-200">{p.name}</div>
                  <div className="text-zinc-500">{(p.volume / 1000).toFixed(2)} см³ · {p.triCount.toLocaleString("ru-RU")} тр.</div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); toggleVisible(p.id); }}
                  title={p.visible ? "Скрыть" : "Показать"}
                  className="text-zinc-400 hover:text-zinc-100 shrink-0 px-1"
                >
                  {p.visible ? "◉" : "◎"}
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); downloadPart(p.id); }}
                  title="Скачать STL"
                  className="text-zinc-400 hover:text-zinc-100 shrink-0 px-1"
                >
                  ⭳
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); deletePart(p.id); }}
                  title="Удалить"
                  className="text-zinc-500 hover:text-rose-400 shrink-0 px-1"
                >
                  ✕
                </button>
              </div>
            ))}
            {partsMeta.length === 0 && (
              <div className="text-xs text-zinc-600 italic px-1 py-2">Пока ничего не загружено</div>
            )}
          </div>
        </div>

        <div className="px-4 py-3 border-b border-zinc-800">
          <span className="block text-xs text-zinc-400 mb-2">Выравнивание выбранной модели</span>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs text-zinc-500">Угол, °</span>
            <input
              type="number"
              value={rotateAngle}
              onChange={(e) => setRotateAngle(parseFloat(e.target.value) || 0)}
              className="w-20 bg-zinc-950 border border-zinc-700 rounded-md px-2 py-1 text-xs text-zinc-200"
            />
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {["x", "y", "z"].map((axis) => (
              <div key={axis} className="flex flex-col gap-1">
                <button
                  onClick={() => rotateSelected(axis, 1)}
                  className="px-2 py-1 text-xs rounded-md bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-200"
                >
                  {axis.toUpperCase()} +
                </button>
                <button
                  onClick={() => rotateSelected(axis, -1)}
                  className="px-2 py-1 text-xs rounded-md bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-200"
                >
                  {axis.toUpperCase()} −
                </button>
              </div>
            ))}
          </div>
          <button
            onClick={centerSelected}
            className="mt-2 w-full px-2 py-1.5 text-xs rounded-md bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-200"
          >
            Центрировать по началу координат
          </button>
        </div>

        <div className="px-4 py-3 border-b border-zinc-800">
          <span className="block text-xs text-zinc-400 mb-2">Инструмент разреза</span>
          <div className="flex gap-1.5 mb-2">
            {["x", "y", "z"].map((axis) => (
              <button
                key={axis}
                onClick={() => setCutAxis(axis)}
                className={
                  "flex-1 px-2 py-1 text-xs rounded-md border " +
                  (cutAxis === axis
                    ? "bg-amber-600/80 border-amber-500 text-white"
                    : "bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700")
                }
              >
                Плоскость {axis.toUpperCase()}
              </button>
            ))}
          </div>
          <input
            type="range"
            min={cutRange.min}
            max={cutRange.max}
            step={(cutRange.max - cutRange.min) / 500 || 0.1}
            value={cutOffset}
            onChange={(e) => setCutOffset(parseFloat(e.target.value))}
            className="w-full accent-amber-500"
          />
          <div className="text-xs text-zinc-500 mb-2">положение: {cutOffset.toFixed(2)} мм</div>
          <button
            onClick={performCut}
            className="w-full px-2 py-1.5 text-xs rounded-md bg-amber-600 hover:bg-amber-500 text-white font-medium"
          >
            Разрезать выбранную модель
          </button>
          <p className="text-xs text-zinc-600 mt-1.5">
            Разрез можно повторять сколько угодно раз — каждая часть становится отдельной моделью.
          </p>
        </div>

        <div className="px-4 py-3 text-xs text-zinc-600 mt-auto">
          Объём считается только для замкнутых (watertight) сеток.
        </div>
      </div>
    </div>
  );
}